import { AlertTriangle } from "lucide-react";
import React, { useState } from "react";
import { useLocation } from "react-router";
import { useMe } from "src/context/MeContext";

interface EvaluationBannerProps {
  suspendedAt: string | null | undefined;
}

const REASON_MESSAGES: Record<string, string> = {
  CREDIT_LIMIT_REACHED: "Your organization has exceeded its credit limit. All agents are paused.",
  SUBSCRIPTION_ENDED: "Your organization's subscription has ended. All agents are paused.",
  EVALUATION_ENDED: "Your organization's evaluation period has ended. All agents are paused.",
};

const DEFAULT_MESSAGE = "Your organization has been suspended. All agents are paused.";

function formatUtcDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

function formatLocalDate(isoDate: string): string {
  const date = new Date(isoDate);
  const dateStr = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const timeStr = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${dateStr} at ${timeStr} (local)`;
}

const ToggleableTimestamp = React.memo<{ isoDate: string }>(function ToggleableTimestamp({ isoDate }) {
  const [showLocal, setShowLocal] = useState(false);
  const label = showLocal ? formatLocalDate(isoDate) : `${formatUtcDate(isoDate)} UTC`;
  return (
    <span
      role="button"
      tabIndex={0}
      className="cursor-pointer border-b border-dotted border-current"
      onClick={() => setShowLocal((v) => !v)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowLocal((v) => !v); }}
    >
      {label}
    </span>
  );
});

export const EvaluationBanner: React.FC<EvaluationBannerProps> = ({ suspendedAt }) => {
  const location = useLocation();
  const me = useMe();

  // Don't show on intern pages
  if (location.pathname.startsWith("/intern")) return null;

  const isSuspended = Boolean(me.organization?.isSuspended);

  // Show suspended banner if org is currently suspended
  if (isSuspended) {
    const reason = me.organization?.suspensionReason;
    const resetAt = me.organization?.creditLimitResetAt;
    const manualSuspensionBlocksReset = suspendedAt && resetAt && new Date(suspendedAt) <= new Date(resetAt);
    const message = (reason && REASON_MESSAGES[reason]) || DEFAULT_MESSAGE;
    const showResetDate = reason === "CREDIT_LIMIT_REACHED" && resetAt && !manualSuspensionBlocksReset;
    return (
      <div className="w-full bg-red-600 px-4 py-2 text-center text-sm text-white flex items-center justify-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>
          {message}
          {showResetDate && (
            <> Usage will reset on <ToggleableTimestamp isoDate={resetAt} />.</>
          )}
        </span>
      </div>
    );
  }

  if (!suspendedAt) return null;

  const suspendedDate = new Date(suspendedAt);
  const now = new Date();

  // Only show banner if suspended_at is in the future
  if (suspendedDate <= now) return null;

  const diffMs = suspendedDate.getTime() - now.getTime();
  const totalHours = Math.ceil(diffMs / (1000 * 60 * 60));
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  // Only show banner if within 7 days
  if (diffDays >= 7) return null;

  const daysRemaining = Math.ceil(diffDays);
  const reason = me.organization?.suspensionReason;

  let timeLeft: string;
  if (daysRemaining > 1) {
    timeLeft = `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}`;
  } else {
    const hoursRemaining = Math.max(1, totalHours);
    timeLeft = `${hoursRemaining} ${hoursRemaining === 1 ? "hour" : "hours"}`;
  }

  const UPCOMING_LABELS: Record<string, string> = {
    SUBSCRIPTION_ENDED: "Your subscription is ending",
    EVALUATION_ENDED: "Your evaluation period is ending",
    CREDIT_LIMIT_REACHED: "Your credit limit has been reached",
  };
  const label = (reason && UPCOMING_LABELS[reason]) || "Your organization will be suspended";

  return (
    <div className="w-full bg-red-600 px-4 py-2 text-center text-sm text-white flex items-center justify-center gap-2">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        {label} in {timeLeft} on <ToggleableTimestamp isoDate={suspendedAt} />
      </span>
    </div>
  );
};
